// @owners { team: patients-team }
import { type QueryClient } from '@tanstack/react-query';
import { mapNextAvailableDateResponsesToDates } from '~shared/features/next-available-date/helpers/mapNextAvailableDateResponsesToDates';
import { nextAvailableDatesQueryKeys } from '~shared/features/next-available-date/queries/nextAvailableDatesQueryKeys';
import { type NextAvailableDateMapping } from '~shared/reducers/nextAvailableDates';
import { type NextAvailableDateResponse } from '~shared/types/clients';

/**
 * Reads every checkout-scoped NAD cache entry from the React Query client and merges them into a single
 * {@link NextAvailableDateMapping} (`Record<string, NextAvailableDate>`) keyed by compound address+prescription ID.
 *
 * Compatible with Redux `state.nextAvailableDates.dates` — use as a drop-in data source when migrating
 * selectors that read the compound-key mapping (strategy **A**).
 *
 * Cache entries are matched via the {@link nextAvailableDatesQueryKeys.checkout} partial key so only
 * `forCheckout` queries are included. Entries with no cached data (e.g. pending or error state) are skipped.
 */
export const mergeCheckoutNextAvailableDateMappingsFromQueryClient = (
  queryClient: QueryClient,
): NextAvailableDateMapping => {
  const entries = queryClient.getQueriesData<NextAvailableDateResponse[]>({
    queryKey: nextAvailableDatesQueryKeys.checkout(),
  });

  return entries.reduce<NextAvailableDateMapping>((acc, [, data]) => {
    if (!data) return acc;
    return { ...acc, ...mapNextAvailableDateResponsesToDates(data) };
  }, {});
};
